import { z } from "zod";

export type SortDirection = "asc" | "desc";

export interface PaginationMeta {
  readonly totalCount: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly firstPageUrl: string;
  readonly lastPageUrl: string;
  readonly nextPageUrl: string | null;
  readonly previousPageUrl: string | null;
}

export interface PaginatedResult<T> {
  readonly data: readonly T[];
  readonly meta: PaginationMeta;
}

const nonEmptyString = z.string().min(1);

export const paginationMetaDtoSchema = z.object({
  totalCount: z.number().int().nonnegative(),
  currentPage: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
  firstPageUrl: nonEmptyString,
  lastPageUrl: nonEmptyString,
  nextPageUrl: z.string().nullable(),
  previousPageUrl: z.string().nullable(),
});

export function paginatedFromDto<TDto, TDomain>(
  response: {
    readonly data: readonly TDto[];
    readonly meta: PaginatedResult<TDto>["meta"];
  },
  map: (dto: TDto) => TDomain,
): PaginatedResult<TDomain> {
  return {
    data: response.data.map(map),
    meta: { ...response.meta },
  };
}

/**
 * Synthesizes pagination meta for an endpoint that returns a plain array
 * (e.g. `GET /modules/sellable`), so callers can still feed a `DataTable`
 * that's hardcoded to `manualPagination`.
 */
export function paginateLocally<T>(
  rows: readonly T[],
  page: number,
  limit: number,
): PaginatedResult<T> {
  const totalCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * limit;

  return {
    data: rows.slice(start, start + limit),
    meta: {
      totalCount,
      currentPage,
      totalPages,
      firstPageUrl: "",
      lastPageUrl: "",
      nextPageUrl: currentPage < totalPages ? "" : null,
      previousPageUrl: currentPage > 1 ? "" : null,
    },
  };
}
